import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { JwtPayload, jwtDecode } from 'jwt-decode'
import { type ReactNode, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Spinner from 'src/@core/components/spinner'
import { loadingAtom } from 'src/atoms'
import client from 'src/client'
import authConfig from 'src/configs/auth'
import { useUser } from 'src/hooks/useUser'
import { useAtomUserMe } from '@/hooks/stores/useAtomUserMe'
import { useSidenav } from './navigation/useSidenav'

type LayoutProps = {
  children: ReactNode
}

export function isAnonymousRoute(pathname: string) {
  return pathname === '/login' || pathname.startsWith('/public')
}

export function Layout(props: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useAtom(loadingAtom)
  const { atomUserMe: meQuery, setAtomUserMe } = useAtomUserMe()

  const queryClient = useQueryClient()

  const meQueryResponse = useUser()

  useSidenav(meQuery.data, location.pathname)

  useEffect(() => {
    if (meQueryResponse.isSuccess && meQueryResponse.data?.success) {
      // A fresh wrapper also signals refetches whose response data is identical.
      setAtomUserMe({ data: { ...meQueryResponse.data } })
      if (!meQueryResponse.data?.data) {
        setLoading(false)
      }
    } else if (meQueryResponse.isError) {
      setLoading(false)
    }
  }, [
    meQueryResponse.data,
    meQueryResponse.dataUpdatedAt,
    meQueryResponse.isSuccess,
    meQueryResponse.isError,
    setAtomUserMe,
    setLoading
  ])

  useEffect(() => {
    async function refreshToken() {
      const isPublicRoute = isAnonymousRoute(location.pathname)

      try {
        type Data = {
          access_token: string
          expires: number
          refresh_token: string
        }

        type RefreshTokenResponse = {
          data: Data
        }

        const refreshToken = localStorage.getItem(authConfig.refreshTokenKeyName)
        if (!refreshToken) {
          if (!isPublicRoute) {
            navigate('/login')
            setLoading(false)
            return
          }

          setLoading(false)
          return
        }

        const token = localStorage.getItem(authConfig.accessTokenKeyName)
        if (token) {
          type Payload = {
            id: string
            role: string
            app_access: boolean
            admin_access: boolean
          } & Pick<JwtPayload, 'iat' | 'exp' | 'iss'>

          const decoded = jwtDecode<Payload>(token)
          if (decoded.exp && Date.now() >= decoded.exp * 1000) {
            const response = await client.api.post<RefreshTokenResponse>('/auth/refresh', {
              refresh_token: localStorage.getItem(authConfig.refreshTokenKeyName),
              mode: 'json'
            })

            localStorage.setItem(authConfig.accessTokenKeyName, response.data.data.access_token)
            localStorage.setItem(authConfig.refreshTokenKeyName, response.data.data.refresh_token)
          }

          if (location.pathname === '/login') {
            navigate('/')
          }
        } else {
          const response = await client.api.post<RefreshTokenResponse>('/auth/refresh', {
            refresh_token: localStorage.getItem(authConfig.refreshTokenKeyName),
            mode: 'json'
          })

          localStorage.setItem(authConfig.accessTokenKeyName, response.data.data.access_token)
          localStorage.setItem(authConfig.refreshTokenKeyName, response.data.data.refresh_token)

          if (location.pathname === '/login') {
            navigate('/')
          }
        }

        if (isPublicRoute) {
          setLoading(false)
        }
      } catch {
        try {
          await client.api.post('/auth/logout', {
            refresh_token: localStorage.getItem(authConfig.refreshTokenKeyName)
          })
        } catch {}
        await queryClient.invalidateQueries()

        localStorage.removeItem(authConfig.accessTokenKeyName)
        if (!isPublicRoute) {
          navigate('/login')
          setLoading(false)
          return
        }

        setLoading(false)
      }
    }

    refreshToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (meQuery.data?.data || meQueryResponse.isFetched || meQueryResponse.isError) {
      setLoading(false)
    }
  }, [meQuery.data, meQueryResponse.isFetched, meQueryResponse.isError, setLoading])

  if (loading) {
    return <Spinner />
  }

  return <>{props.children}</>
}

export function getLayout(page: ReactNode) {
  return <Layout>{page}</Layout>
}
